import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../../hooks';
import proofkitFilemakerService from '../../../services/proofkitFilemakerService';
import { RefreshCw, Plus } from "lucide-react";
import { Button } from '../../../components/ui';
import { YoolTable, StatusPill, MonoText, RowActions, IconButton } from '../../../components/ui/YoolTable';

const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function TemplatesList() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await proofkitFilemakerService.getAllRecords(TEMPLATES_LAYOUT);
      setTemplates(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las plantillas desde FileMaker",
        variant: "destructive",
      });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const templatesData = useMemo(() => {
    return (Array.isArray(templates) ? templates : []).map((r) => {
      const fieldData = r?.fieldData || {};
      return {
        id: r?.recordId || "",
        key: fieldData?.ClavePrincipal || "",
        nombre: fieldData?.nombre || "",
        asunto: fieldData?.asunto || "",
        tipo: fieldData?.tipo || "email",
        estado: fieldData?.estado || "activo",
        fecha_creacion: fieldData?.fecha_creacion || "",
      };
    });
  }, [templates]);

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) {
      return;
    }

    try {
      await proofkitFilemakerService.deleteRecord(TEMPLATES_LAYOUT, templateId);
      toast({
        title: "Plantilla eliminada",
        description: "La plantilla fue eliminada exitosamente",
      });
      fetchTemplates();
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la plantilla",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Plantillas</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => navigate("/campanas/templates/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Plantilla
          </Button>
        </div>
      </div>

      <YoolTable
        columns={[
          { key: 'nombre',         label: 'Nombre',          sortable: true },
          { key: 'asunto',         label: 'Asunto',          wrap: true },
          { key: 'tipo',           label: 'Tipo',
            render: t => <MonoText>{t.tipo || 'email'}</MonoText> },
          { key: 'estado',         label: 'Estado',
            render: t => <StatusPill estado={t.estado} /> },
          { key: 'fecha_creacion', label: 'Fecha Creación' },
          { key: 'acciones',       label: '', align: 'right',
            render: t => (
              <RowActions>
                <IconButton icon="eye"   title="Ver"      onClick={() => navigate(`/campanas/templates/${t.id}`)} />
                <IconButton icon="edit"  title="Editar"   onClick={() => navigate(`/campanas/templates/${t.id}/edit`)} />
                <IconButton icon="trash" title="Eliminar" danger onClick={() => handleDeleteTemplate(t.id)} />
              </RowActions>
            )},
        ]}
        rows={loading ? [] : templatesData}
        rowKey="id"
        search={{ placeholder: 'Buscar plantillas…', keys: ['nombre', 'asunto', 'tipo'] }}
        empty={loading
          ? { icon: 'refresh', title: 'Cargando plantillas…', text: '' }
          : { icon: 'search', title: 'Sin plantillas', text: 'No hay plantillas de email creadas aún.' }}
      />
    </div>
  );
}
